import { useId, useState } from "react";
import type { FormEvent } from "react";
import { api, unwrap } from "../../api/client";
import { ErrorMessage, Loading } from "../../components/ui";
import { t } from "../../i18n/t";
import { formatDateTime, runStatusLabel, runSummaryText } from "../../lib/format";
import { useQuery } from "../../lib/useQuery";

export function RunsTab() {
  const id = useId();
  const [filter, setFilter] = useState("");
  const [kommune, setKommune] = useState("");
  const runs = useQuery(`admin:runs:${kommune}`, (signal) =>
    unwrap(
      api.GET("/api/admin/runs", {
        params: { query: kommune ? { kommune, limit: 100 } : { limit: 100 } },
        signal,
      }),
    ),
  );

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setKommune(filter.trim());
  }

  return (
    <div>
      <form className="toolbar" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor={`${id}-kommune`}>{t("admin.runsFilter")}</label>
          <div className="input-row">
            <input
              id={`${id}-kommune`}
              inputMode="numeric"
              maxLength={4}
              className="input-short"
              placeholder={t("admin.runsFilterAll")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              {t("common.search")}
            </button>
            <button type="button" className="btn btn-secondary" onClick={runs.reload}>
              {t("admin.refresh")}
            </button>
          </div>
        </div>
      </form>
      {runs.error && <ErrorMessage error={runs.error} onRetry={runs.reload} />}
      {!runs.data && !runs.error && <Loading />}
      {runs.data && runs.data.length === 0 && <p>{t("admin.runsEmpty")}</p>}
      {runs.data && runs.data.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <caption className="visually-hidden">{t("admin.runsCaption")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("admin.colStarted")}</th>
                <th scope="col">{t("admin.colKommune")}</th>
                <th scope="col">{t("admin.colStatus")}</th>
                <th scope="col">{t("admin.colReason")}</th>
                <th scope="col">{t("admin.colVersion")}</th>
                <th scope="col">{t("admin.colFinished")}</th>
                <th scope="col">{t("admin.colResult")}</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.map((r) => (
                <tr key={r.id}>
                  <th scope="row" className="nowrap">
                    {formatDateTime(r.started_at)}
                  </th>
                  <td>{r.kommunenummer}</td>
                  <td>
                    <span className={`run-status run-${r.status}`}>{runStatusLabel(r.status)}</span>
                    {r.synthetic && (
                      <span className="badge badge-synthetic">{t("admin.synthetic")}</span>
                    )}
                  </td>
                  <td>{r.reason}</td>
                  <td>
                    {t("admin.versionValue", {
                      engine: r.engine_version ?? t("common.dash"),
                      criteria: r.criteria_version ?? t("common.dash"),
                    })}
                  </td>
                  <td className="nowrap">{formatDateTime(r.finished_at)}</td>
                  <td>
                    {r.error ? (
                      <pre className="error-text">{r.error}</pre>
                    ) : (
                      (runSummaryText(r.summary) ?? t("common.dash"))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
